import type { ComponentPropsWithoutRef, ReactNode } from 'react';

type Tone = 'ink' | 'blue' | 'green' | 'coral' | 'soft';

const TONE_COLOR: Record<Tone, string> = {
  ink: 'var(--ink)',
  blue: 'var(--blue)',
  green: 'var(--green)',
  coral: 'var(--coral)',
  soft: 'var(--muted-soft)',
};

interface PhotoPlateProps extends ComponentPropsWithoutRef<'div'> {
  /** The photo. Without it the plate renders `children` — a spinner, initials. */
  src?: string | null;
  alt?: string;
  tone?: Tone;
  /** Corner crop marks. Off for a plate that is only a thumbnail. */
  ticks?: boolean;
  /** Crop marks in their own colour — they read as an annotation on the
   *  frame, not as part of it, so they carry the accent while the frame
   *  stays hairline ink. Defaults to the frame's tone. */
  tickTone?: Tone;
  /** Dashed frame: this plate holds no photo, or is waiting for a dropped one. */
  dashed?: boolean;
  children?: ReactNode;
}

/**
 * One square framed photo — the unit this card is built from. Every photo in
 * the card is a plate: the one Entra holds, the one proposed to replace it,
 * the crop staged in the browser.
 *
 * Square rather than the 35×45 of a passport print, because square is what
 * Entra stores and what the crop step produces: a 35:45 frame over a 1:1 image
 * would crop the sides in the viewer and show a photo nobody uploaded. The ID
 * vernacular is carried by the corner crop marks instead, which sit outside
 * the frame and cost the image nothing.
 */
export function PhotoPlate({
  src,
  alt,
  tone = 'ink',
  ticks = false,
  tickTone,
  dashed = false,
  children,
  className,
  style,
  ...rest
}: PhotoPlateProps) {
  const color = TONE_COLOR[tone];
  const tickColor = TONE_COLOR[tickTone ?? tone];

  return (
    <div
      className={`relative aspect-square w-full bg-card text-ink ${className ?? ''}`}
      style={{
        border: `1px ${dashed ? 'dashed' : 'solid'} ${color}`,
        ...style,
      }}
      {...rest}
    >
      {src ? (
        <img
          src={src}
          alt={alt ?? ''}
          className="block size-full object-cover"
        />
      ) : (
        <div className="flex size-full flex-col items-center justify-center gap-1">
          {children}
        </div>
      )}

      {ticks && (
        <>
          <Tick color={tickColor} top left />
          <Tick color={tickColor} top />
          <Tick color={tickColor} left />
          <Tick color={tickColor} />
        </>
      )}
    </div>
  );
}

/** One crop mark, sitting just outside the frame's corner. */
function Tick({
  color,
  top = false,
  left = false,
}: {
  color: string;
  top?: boolean;
  left?: boolean;
}) {
  return (
    <span
      aria-hidden="true"
      className="absolute size-[10px] border-0 border-solid"
      style={{
        borderColor: color,
        [top ? 'top' : 'bottom']: -5,
        [left ? 'left' : 'right']: -5,
        [top ? 'borderTopWidth' : 'borderBottomWidth']: 2,
        [left ? 'borderLeftWidth' : 'borderRightWidth']: 2,
      }}
    />
  );
}
